import { activeStatus, displayInBlock } from '../../constants/events';
import {
  Action,
  City,
  Compilation,
  Material,
  Page,
  Person,
  Place,
  Social,
} from '../../models';
import { AdpCategories, Category } from '../../models/categories';
import { MainSlide } from '../../models/city';
import { Contact } from '../../models/contact';
import { PersonInListV3 } from '../../models/person';
import { Specialization } from '../../models/specialization';
import { http } from '../api/http';
import { V2, V3, V4 } from '../api/urls';
import { CITIES_API_LIMIT } from '../consts';
import { SearchResultsApi } from '../../models/search';
import { ActionList } from '../../models/action';

export function getCities() {
  return http.get<{ results: City[] }>(V3.cities, {
    limit: CITIES_API_LIMIT,
  });
}

export function getAdpCategories(cityId: number) {
  return http.get<{ categories: AdpCategories }>(V3.adpCategories(cityId));
}

export function getParentCategories(cityId: number) {
  return http.get<{ results: Category[] }>(V3.categories, {
    city: cityId,
    is_web: true,
    status: activeStatus,
    sort: 'sortOrder',
    limit: 1000,
    level: 0,
  });
}

export function getFooterPages() {
  return http.get<{ results: Page[] }>(V2.pages, {
    city: null,
    status: activeStatus,
    show_in_footer: true,
  });
}

export function getSocials(cityId: number) {
  return http.get<{ results: Social[] }>(V2.socials, {
    display_in_block: displayInBlock.contactUs,
    status: activeStatus,
    city: cityId,
  });
}

export function getSocialLinks(cityId: number) {
  return http.get<{ results: Social[] }>(V2.socials, {
    display_in_block: displayInBlock.weInSocials,
    status: activeStatus,
    city: cityId,
  });
}

export function getContacts(cityId: number) {
  return http.get<{ results: Contact[] }>(V2.contacts, {
    limit: 9e9,
    status: activeStatus,
    city: cityId,
  });
}

export function getPersonBySlug(slug: string) {
  return http.get<Person>(V3.personBySlug(slug));
}

export function getPlaceBySlug(slug: string) {
  return http.get<Place>(V2.placeBySlug(slug));
}

export function getPersons(params: {
  limit?: number;
  offset?: number;
  place__in?: number;
}) {
  return http.get<{
    results: PersonInListV3[];
  }>(V3.persons, params);
}

export function getCompilations(params: {
  city?: number;
  status?: string;
  is_web?: string;
  limit?: number;
  ordering?: string;
  exclude?: number;
}) {
  return http.get<{
    results: Compilation[];
    next: string | null;
  }>(V3.compilations.root, params);
}

export function getCompilationBySlug(
  slug: string,
  params: {
    limit?: number;
  }
) {
  return http.get<Compilation>(V3.compilations.slug(slug), params);
}

export function getEvents(
  params: {
    ids?: string;
    is_web?: boolean;
    status?: string | string[];
    offset?: number;
    limit?: number;
    city?: number;
    places?: number;
    ordering?: string;
    exclude_type?: string;
    filter_price__gte?: number;
    filter_price__lte?: number;
    date_from?: string;
    date_to?: string;
    with_cards?: boolean;
    categories?: string | number[];
    persons?: number;
    pref_city?: number;
    q?: string;
    remote_id?: string | number;
  },
  options?: {
    withTokenRequest?: boolean;
    arrayFormat?: 'repeat' | 'indices' | 'brackets' | 'comma';
  }
) {
  return http.get<ActionList>(V4.events, params, null, options);
}

export function getSearch(
  params: {
    offset: number;
    content_type: string;
    query: string;
    limit: number;
    city?: number;
    sort?: string[];
    status: string;
  },
  options?: { withTokenRequest?: boolean }
) {
  return http.get<SearchResultsApi>(V2.search, params, null, options);
}

export function getEventBySlug(slug: string) {
  return http.get<Action>(V3.eventsSlug(slug));
}

export function getPage(slug: string) {
  return http.get<Page>(`${V2.pages}${slug}/`);
}

export function getSpecializations(params: { is_active?: boolean }) {
  return http.get<{ results: Specialization[] }>(V2.specializations, params);
}

export function getPlaces(params: {
  is_web?: boolean;
  city?: string | number;
  show_on_index?: boolean;
  offset?: number;
  limit?: number;
  status?: string;
  ordering?: string;
  categories?: number[];
}) {
  return http.get<{
    results: Place[];
    next: string | null;
  }>(V2.places, params);
}

export function getMaterials(params: {
  is_web?: boolean;
  city?: number;
  offset?: string | number;
  limit?: number;
  status?: string;
  ordering?: string;
}) {
  return http.get<{
    results: Material[];
    next: string | null;
  }>(V3.materials, params);
}

export function getMainSlide(cityId: number | string) {
  return http.get<MainSlide>(`${V4.cities + cityId}/main-slide/`);
}
